import { Inject, Injectable } from '@angular/core';
import { Actions, Effect } from '@ngrx/effects';
import * as auth from '../actions/logout.actions';
import * as config from '../../modules/config/config.actions';
import { DOCUMENT } from '@angular/common';
import { AppState } from '../../app.state';
import { Store } from '@ngrx/store';
import { selectConfig } from '../../modules/config/config.selector';
import { ConfigModel } from '../../modules/config/config.model';
import { TokenRestClient } from '../services/token-rest-client.service';
import { LoggerService } from '../services/logger.service';
import { SourceAppService } from '../services/source-app.service';
import { AuthenticationOidcService } from '../../modules/url-params/authentication-oidc.service';
import * as RouterActions from '../actions/router.actions';

import 'rxjs/add/operator/switchMap';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/catch';
import 'rxjs/add/observable/of';
import 'rxjs/add/operator/combineLatest';
import 'rxjs/add/operator/withLatestFrom';
import { LogoutService } from '../../modules/shared/services/logout.service';

/**
 * Effects related to logout action.
 */
@Injectable()
export class LogoutEffects {
    private configModel: ConfigModel;

    /**
     * Config has to be remembered in a local property - it is not available in logout$ effect bellow.
     *
     * The logout action cleans the store and effect logout$ has no access to otherwise.
     */
    @Effect({dispatch: false}) rememberConfig$ = this.actions$
        .ofType(config.LOAD_SUCCESS)
        .withLatestFrom(this.store.select(selectConfig))
        .do(([, config]: ([any, ConfigModel])) => this.configModel = config);

    /**
     * Logout - take uri and do redirect - store is cleared by a meta reducer just before this effect starts so that
     * configModel has to be remembered.
     */
    @Effect({dispatch: false}) logoutAndRedirect$ = this.actions$
        .ofType(auth.LOGOUT)
        .filter(() => !this.sourceService.isFromCps())
        .do(() => {
            if (this.sourceService.isOidc()) {
                this.authOidc.logout();
            } else {
                const logoutUrl = this.logoutService.getLogoutUrl(this.configModel);
                this.client.revokeToken()
                    .subscribe(() => this.redirect(logoutUrl),
                        error => {
                            this.logger.error('Revoke of the access token from MEP failed.', error);
                            this.redirect(logoutUrl);
                        }
                    );
            }
        });

    @Effect({dispatch: false}) logoutAndStay$ = this.actions$
        .ofType(auth.LOGOUT)
        .filter(() => this.sourceService.isFromCps())
        .do(() => {
            this.client.revokeToken()
                .subscribe(() => this.logger.log('Revoke of the access token from ADFS succeeded.'),
                    error => {
                        this.logger.error('Revoke of the access token from ADFS failed.', error);
                    }
                );
        });

    @Effect() routeToLogoutScreen$ = this.actions$
        .ofType(auth.LOGOUT)
        .filter(() => this.sourceService.isFromCps())
        .map(() => new RouterActions.Go({
                path: ['logout']
        }));

    /**
     * Listen for redirect action and then redirect to the url carried in its payload.
     */
    @Effect({dispatch: false}) redirect$ = this.actions$
        .ofType(auth.REDIRECT)
        .do((action: auth.RedirectAction) => this.redirect(action.payload));

    /**
     * Function redirects to specified URI.
     * @param logoutURI
     */
    private redirect(logoutURI: string) {
        this.document.location.href = logoutURI;
    }

    constructor(private store: Store<AppState>,
                private actions$: Actions,
                private client: TokenRestClient,
                private logger: LoggerService,
                private sourceService: SourceAppService,
                private authOidc: AuthenticationOidcService,
                private logoutService: LogoutService,
                @Inject(DOCUMENT) private document: any
    ) {
    }

}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/@angularclass/hmr-loader!./~/angular-router-loader/src!./~/tslint-loader!./src/app/core/effects/logout.effects.ts